import React, { useCallback, useState } from "react";
import BasicHomeSiteDetails from "./components/BasicHomeSiteDetails";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import { closingsMyConciergeOverviewBuyers } from "../../../utils/fields";
import CoreHomeSiteDetails from "./components/CoreHomeSiteDetails";
import { useDialogBoxContext } from "../../../components/decisionDialogBox/decisionDialogBoxContext";
import { decisionDialogBoxTitle, decisionDialogBoxMessage } from "../../../components/decisionDialogBox/decisionDialogBox";
import { useSnackbarContext } from "../../../components/globalSnackNotify/globalSnackNotifyContext";
import { salesforceUrl } from "../../../api/apiUrl";

interface HomeSiteFullDetailsProps {
  setShowModal: (value: boolean) => void;
  setInformationModal: (value: any) => void;
  title: string;
  homeSiteDetails: any;
  userId: string;
  tab?: string;
  milestoneFilter?: string;
  expandMilestone?: string;
}

const HomeSiteFullDetails: React.FC<HomeSiteFullDetailsProps> = ({
  setShowModal,
  setInformationModal,
  title,
  homeSiteDetails,
  userId,
  tab,
  milestoneFilter,
  expandMilestone,
}) => {
  const theme = useTheme();
  
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { showDecisionDialogBox } = useDialogBoxContext();
  const [unsavedChangesInAdditional, setUnsavedChangesInAdditional] =
    useState(false);
  const [childSave, setChildSave] = useState<() => Promise<void>>(
    () => async () => {}
  );
  const [childCancel, setChildCancel] = useState<() => void>(() => {});
  const { showSnackbar } = useSnackbarContext();
  
  const generalInformation = [
    {
      label: "Community Name",
      value:
        homeSiteDetails?.[
          closingsMyConciergeOverviewBuyers.communityName.field
        ] || "",
    },
    { label: "Community Phase", value: homeSiteDetails?.communityPhase || "" },
    { label: "Homesite Phase", value: homeSiteDetails?.homesitePhase || "" },
    { label: "Homesite #", value: homeSiteDetails?.homeSiteId || "" },
    { 
      label: "Opportunity ID", 
      value: homeSiteDetails?.opportunityId || "",
      link: homeSiteDetails?.opportunityId ? `${salesforceUrl}/${homeSiteDetails.opportunityId}/view` : ""
    },
    {
      label: "Homesite Address",
      value: homeSiteDetails?.homesiteAddress || "",
    },
    {
      label: "Revenue Subtotal",
      value: homeSiteDetails?.revenueSubtotal
        ? `$ ${homeSiteDetails.revenueSubtotal.toLocaleString("en-us", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : "",
    },
  ];

  const handleChildHandlersReady = useCallback(
    (handlers: { save: () => Promise<void>; cancel: () => void }) => {
      setChildSave(() => handlers.save);
      setChildCancel(() => handlers.cancel);
    },
    []
  );

  /* Update the state to reflect if there are unsaved changes in the additional details */
  const onUnsavedChangesChange = (hasUnsaved: boolean) => {
    setUnsavedChangesInAdditional(hasUnsaved);
  };

  /* Handle closing the modal, checking for unsaved changes */
  const handleClose = () => {
    if (unsavedChangesInAdditional) {
      showDecisionDialogBox(
        true,
        decisionDialogBoxTitle,
        decisionDialogBoxMessage,
        async () => {
          await childSave();
          setShowModal(false);
          setInformationModal(null);
          showSnackbar("Changes saved successfully!", "success");
        },
        () => {
          childCancel();
          setShowModal(false);
          setInformationModal(null);
          showSnackbar("Changes Discarded", "info");
        }
      );
    } else {
      setShowModal(false);
      setInformationModal(null);
    }
  };

  return (
    <Box
      sx={{
        width: "1100px",
        height: "800px",
        position: "absolute",
        top: "1784px",
        left: "400px",
        padding: "40px 0 0 0",
        gap: "12px",
        borderRadius: "16px 0 0 0",
        opacity: 0,
      }}
    >
      <Dialog
        open
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            handleClose();
          }
        }}
        maxWidth="lg"
        sx={{
          borderRadius: "16px 0px 0px 0px",
          "& .MuiPaper-root": { backgroundColor: "#FAFBFC", color: "black" },
        }}
        fullScreen={fullScreen}
      >
        <React.Fragment>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="h6">
                {homeSiteDetails?.buyers?.[0]?.firstName}
                {"  "}
                {homeSiteDetails?.buyers?.[0]?.lastName}
              </Typography>
              <Box display="flex" alignItems="center" marginLeft="4px">
                <Tooltip
                  title={
                    homeSiteDetails?.buyers?.[0]?.email || "Email Unavailable"
                  }
                  arrow
                  placement="right"
                >
                  <EmailIcon style={{ color: "#005DAA" }} />
                </Tooltip>
              </Box>
            </Box>
            <Tooltip title="Close" arrow placement="left">
              <IconButton
                onClick={() => {
                  handleClose();
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent
            sx={{
              overflowY: "scroll",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <BasicHomeSiteDetails generalInformation={generalInformation} />
            <CoreHomeSiteDetails
              userId={userId}
              homeSiteDetails={homeSiteDetails}
              tab={tab}
              milestoneFilter={milestoneFilter}
              expandMilestone={expandMilestone}
              onChildHandlersReady={handleChildHandlersReady}
              onUnsavedChangesChange={onUnsavedChangesChange}
            />
          </DialogContent>
        </React.Fragment>
      </Dialog>
    </Box>
  );
};

export default HomeSiteFullDetails;